import React, { useRef, useEffect, useState } from 'react';
import { TransformControls } from '@react-three/drei';
import { useThree } from '@react-three/fiber';
import { SceneState, useSceneStore } from '../../state/sceneStore';
import { Obstacle as ObstacleType } from '../../api/models';
import { Euler, Mesh } from 'three';
import type { TransformControls as TransformControlsImpl } from 'three-stdlib';

/** Matches backend SENSOR_EULER_ORDER ('xyz'): yaw about global Z, outermost. */
const OBSTACLE_EULER_ORDER = 'ZYX' as const;

interface ObstacleProps {
  id: string;
}

const Obstacle: React.FC<ObstacleProps> = ({ id }) => {
  const obstacle = useSceneStore((state: SceneState) =>
    state.obstacles.find((o: ObstacleType) => o.id === id)
  );
  const selectedObjectId = useSceneStore((state: SceneState) => state.selectedObjectId);
  const setSelectedObjectId = useSceneStore((state: SceneState) => state.setSelectedObjectId);
  const updateObstacleProperties = useSceneStore(
    (state: SceneState) => state.updateObstacleProperties
  );
  const setIsDragging = useSceneStore((state: SceneState) => state.setIsDragging);
  const isDragging = useSceneStore((state: SceneState) => state.isDragging);

  const isSelected = selectedObjectId === id;
  const transformMode = useSceneStore((state: SceneState) => state.transformMode);
  const isAlignmentMode = useSceneStore((state: SceneState) => state.isAlignmentMode);
  const controlsRef = useRef<TransformControlsImpl>(null);
  const [target, setTarget] = useState<Mesh | null>(null);
  const { camera } = useThree();

  useEffect(() => {
    const controls = controlsRef.current;
    if (controls) {
      const callback = (event: { value: boolean }) => {
        const dragging = event.value;
        setIsDragging(dragging);
        
        // Immediately disable OrbitControls when dragging starts
        if (dragging) {
          const canvas = document.querySelector('canvas');
          if (canvas) {
            canvas.style.pointerEvents = 'none';
          }
          
          // Directly disable OrbitControls immediately
          const orbitControls = document.querySelector('canvas')?.parentElement?.querySelector('[data-orbit-controls]');
          if (orbitControls) {
            (orbitControls as any).enabled = false;
          }
        } else {
          // Re-enable pointer events when dragging stops
          const canvas = document.querySelector('canvas');
          if (canvas) {
            canvas.style.pointerEvents = 'auto';
          }
        }
      };
      (controls as any).addEventListener('dragging-changed', callback);
      return () => (controls as any).removeEventListener('dragging-changed', callback);
    }
  }, [target, setIsDragging, isSelected, camera]);

  if (!obstacle) {
    return null;
  }

  return (
    <>
      <mesh
        ref={setTarget}
        position={[obstacle.position[0], obstacle.position[1], obstacle.position[2]]}
        rotation={new Euler(...obstacle.rotation, OBSTACLE_EULER_ORDER)}
        onClick={(e) => {
          e.stopPropagation();
          setSelectedObjectId(id);
        }}
      >
        <boxGeometry args={obstacle.size} />
        <meshStandardMaterial color={isSelected ? 'orange' : 'green'} />
      </mesh>
      {isSelected && target && !isAlignmentMode && (
        <TransformControls
          mode={transformMode}
          space={transformMode === 'rotate' ? 'local' : 'world'}
          object={target}
          ref={controlsRef}
          onChange={() => {
            if (target && obstacle && isDragging) {
              const bottomZ = target.position.z - obstacle.size[2] / 2;
              if (bottomZ < 0) {
                target.position.z = obstacle.size[2] / 2;
              }
              const { position, rotation } = target;
              updateObstacleProperties(id, {
                position: [position.x, position.y, position.z] as [number, number, number],
                rotation: [rotation.x, rotation.y, rotation.z],
              });
            }
          }}
        />
      )}
    </>
  );
};

export default Obstacle;
